import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Card, Skeleton, Typography } from '@mui/material';
import { AccessTime, WaterDrop } from '@mui/icons-material';
import { MatchWeatherData } from '@/services/scheduleService';
import { getWeatherIcon, getAqiColor, WEATHER_ICON_VERSION } from '@/utils/weatherIcons';
import { getWeatherAlert } from '@/utils/weatherContext';
import { convertTemp, tempUnitLabel } from '@/utils/temperatureUtils';
import { RootState } from '@/store';


/**
 * Animated thermometer icon with temperature-sensitive mercury level.
 * Mercury height scales with temp (20°F=low, 100°F=full). Color is blue <55°F, red otherwise.
 */
const ThermometerIcon: React.FC<{ size: number; temperature: number | null }> = ({ size, temperature }) => {
  const temp = temperature ?? 70;
  const isCold = temp < 55;
  const color = isCold ? '#3b82f6' : '#ef4444';

  // Mercury top: y=170 at 20°F (low), y=40 at 100°F (full tube)
  // Clamp temp to 20-100 range for mapping
  const clamped = Math.max(20, Math.min(100, temp));
  const mercuryTop = 170 - ((clamped - 20) / 80) * 130;
  const mercuryLength = 219 - mercuryTop;

  return (
    <svg width={size} height={size} viewBox="0 0 512 512">
      <g transform="translate(197 117)">
        <circle cx="59" cy="219" r="36" fill={color}/>
        <path fill="none" stroke={color} strokeLinecap="round" strokeMiterlimit="10" strokeWidth="24" d={`M59 ${Math.round(mercuryTop)}v${Math.round(mercuryLength)}`}/>
        <path fill="rgba(107,114,128,0.1)" stroke="#d4d7dd" strokeLinecap="round" strokeLinejoin="round" strokeWidth="6" d="M115 218.2c0 31.4-25 56.8-56 56.8S3 249.6 3 218.2a57 57 0 0124-46.6V35.5a32 32 0 1164 0v136a57 57 0 0124 46.7ZM63 83h28M63 51h28m-28 64h28"/>
      </g>
    </svg>
  );
};

/**
 * Inline UV icon — sun with radiating lines, colored by WHO/EPA UV index scale:
 *   Moderate (3-5): yellow #F9A825, 8 rays
 *   High (6-7): orange #E65100, 12 rays
 *   Very High (8+): red #D32F2F, 16 rays
 */
const UvIcon: React.FC<{ size: number; uv: number }> = ({ size, uv }) => {
  const rounded = Math.round(uv);
  const color = rounded >= 11 ? '#9936d4' : rounded >= 8 ? '#ff3c00' : rounded >= 6 ? '#ff8d00' : '#ffb800';
  const rayCount = rounded >= 8 ? 16 : rounded >= 6 ? 12 : 8;
  const rayAngles = Array.from({ length: rayCount }, (_, i) => i * (360 / rayCount));
  const r = 3; // sun radius
  const ri = 4.5; // ray inner
  const ro = 6.5; // ray outer
  return (
    <svg width={size} height={size} viewBox="0 0 16 16">
      <circle cx="8" cy="8" r={r} fill={color} />
      {rayAngles.map((deg) => {
        const rad = (deg * Math.PI) / 180;
        return (
          <line
            key={deg}
            x1={8 + ri * Math.cos(rad)} y1={8 + ri * Math.sin(rad)}
            x2={8 + ro * Math.cos(rad)} y2={8 + ro * Math.sin(rad)}
            stroke={color} strokeWidth="1.5" strokeLinecap="round"
          />
        );
      })}
    </svg>
  );
};

interface WeatherIconProps {
  weather?: MatchWeatherData;
  dateTime: string | null;
  status?: string;
}

/**
 * Time-of-day icon next to match time.
 * Horizon for daytime, starry-night for nighttime.
 */
export const WeatherIcon: React.FC<WeatherIconProps> = React.memo(({ weather, dateTime, status }) => {
  // TBD matches show clock icon
  if (!dateTime || status === 'TBD') {
    return <AccessTime sx={{ fontSize: 20, color: 'text.secondary' }} />;
  }

  const hour = new Date(dateTime).getHours();
  const isDaytime = hour >= 6 && hour < 18;

  // If weather data available, show actual weather icon instead of time-of-day
  if (weather?.forecastAvailable && weather.shortForecast) {
    const { src: weatherSrc } = getWeatherIcon(weather.shortForecast, isDaytime);
    return <img src={weatherSrc} width={36} height={36} alt="" style={{ verticalAlign: 'text-bottom', marginRight: -4 }} />;
  }

  const src = isDaytime ? `/weather-icons/horizon.svg?v=${WEATHER_ICON_VERSION}` : `/weather-icons/moonrise.svg?v=${WEATHER_ICON_VERSION}`;
  const alt = isDaytime ? 'Daytime' : 'Nighttime';

  return <img src={src} width={36} height={36} alt={alt} style={{ verticalAlign: 'text-bottom', marginRight: -4 }} />;
});

interface WeatherInlineProps {
  weather?: MatchWeatherData;
  dateTime?: string | null;
  loading?: boolean;
  onClick?: (e: React.MouseEvent) => void;
}

/**
 * Dark navy gradient weather card for desktop — inline on match card.
 * Shows icon + temp + forecast, metrics, and hourly timeline.
 */
const WeatherSkeleton: React.FC<{ mobile?: boolean }> = ({ mobile }) =>
  mobile ? (
    <Card variant="outlined" sx={{ borderRadius: 99 }}>
      <Box sx={{ px: 1.5, py: 0.75, display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: 1, flexShrink: 0 }} />
        <Skeleton variant="text" width={48} height={28} />
        <Box sx={{ ml: 'auto', display: 'flex', flexDirection: 'column', gap: 0.25, alignItems: 'flex-end' }}>
          <Skeleton variant="text" width={100} height={14} />
          <Skeleton variant="text" width={80} height={14} />
        </Box>
      </Box>
    </Card>
  ) : (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: 1, flexShrink: 0 }} />
      <Skeleton variant="text" width={48} height={28} />
      <Skeleton variant="text" width={100} height={14} sx={{ ml: 1 }} />
    </Box>
  );

export const WeatherInline: React.FC<WeatherInlineProps> = React.memo(({ weather, loading, onClick }) => {
  const temperatureUnit = useSelector((state: RootState) => state.auth.user?.temperatureUnit) || 'FAHRENHEIT';

  if (loading) return <WeatherSkeleton />;
  if (!weather?.forecastAvailable) return null;

  const alert = getWeatherAlert(weather);
  if (!alert) return null;

  const metrics: React.ReactNode[] = [];
  if (weather.humidity != null) {
    metrics.push(
      <Box key="humidity" sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
        <WaterDrop sx={{ fontSize: 14, color: 'text.disabled' }} />
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 600 }}>{weather.humidity}%</Typography>
      </Box>
    );
  }
  if (weather.aqi != null) {
    metrics.push(
      <Box key="aqi" sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: getAqiColor(weather.aqi) }} />
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 600 }}>AQI {weather.aqi}</Typography>
      </Box>
    );
  }
  if (weather.uvIndex != null && Math.round(weather.uvIndex) >= 4) {
    metrics.push(
      <Box key="uv" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
        <UvIcon size={14} uv={weather.uvIndex} />
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 600 }}>UV</Typography>
      </Box>
    );
  }
  if (weather.precipChance != null && weather.precipChance > 0 && hasPrecipForecast(weather.shortForecast)) {
    metrics.push(
      <Box key="rain" sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
        <span style={{ fontSize: 12 }}>☔</span>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 600 }}>{weather.precipChance}%</Typography>
      </Box>
    );
  }

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex', alignItems: 'center', gap: 0.75,
        borderRadius: 99, pl: 1, pr: 1.5, py: 0.25, ml: -1, mr: -1.5, my: -0.25,
        transition: 'background-color 0.15s',
        ...(onClick && {
          cursor: 'pointer',
          WebkitTapHighlightColor: 'transparent',
          '&:hover': { bgcolor: 'action.hover' },
          '&:active': { opacity: 0.85 },
        }),
      }}
    >
      <span style={{ margin: '-10px -12px', flexShrink: 0, display: 'inline-flex' }}><ThermometerIcon size={42} temperature={weather.temperature} /></span>
      <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1, flexShrink: 0, color: 'text.primary', ml: 1 }}>
        {weather.temperature != null ? `${convertTemp(weather.temperature, temperatureUnit)}${tempUnitLabel(temperatureUnit)}` : ''}
      </Typography>
      <DiagonalDivider />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
          {weather.shortForecast && (
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', ml: '3px' }}>
              {weather.shortForecast}
            </Typography>
          )}
          {metrics.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {metrics}
            </Box>
          )}
      </Box>
    </Box>
  );
});

interface WeatherAlertStripProps {
  weather?: MatchWeatherData;
  dateTime?: string | null;
  loading?: boolean;
  onClick?: () => void;
}

/**
 * Compact weather strip for mobile — 1b layout.
 * Icon + temp left, forecast + metrics right. No card wrapper.
 */
export const WeatherAlertStrip: React.FC<WeatherAlertStripProps> = React.memo(({ weather, loading, onClick }) => {
  const temperatureUnit = useSelector((state: RootState) => state.auth.user?.temperatureUnit) || 'FAHRENHEIT';

  if (loading) return <WeatherSkeleton mobile />;
  if (!weather?.forecastAvailable) return null;

  const alert = getWeatherAlert(weather);
  if (!alert) return null;

  const metrics: React.ReactNode[] = [];
  if (weather.humidity != null) {
    metrics.push(
      <Box key="humidity" sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
        <WaterDrop sx={{ fontSize: 12, color: 'text.disabled' }} />
        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 600 }}>{weather.humidity}%</Typography>
      </Box>
    );
  }
  if (weather.aqi != null) {
    metrics.push(
      <Box key="aqi" sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: getAqiColor(weather.aqi) }} />
        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 600 }}>AQI {weather.aqi}</Typography>
      </Box>
    );
  }
  if (weather.uvIndex != null && Math.round(weather.uvIndex) >= 4) {
    metrics.push(
      <Box key="uv" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
        <UvIcon size={12} uv={weather.uvIndex} />
        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 600 }}>UV</Typography>
      </Box>
    );
  }
  if (weather.precipChance != null && weather.precipChance > 0 && hasPrecipForecast(weather.shortForecast)) {
    metrics.push(
      <Box key="rain" sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
        <span style={{ fontSize: 10 }}>☔</span>
        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 600 }}>{weather.precipChance}%</Typography>
      </Box>
    );
  }

  return (
    <Card
      variant="outlined"
      onClick={onClick}
      sx={{
        borderRadius: 99,
        ...(onClick && { cursor: 'pointer', WebkitTapHighlightColor: 'transparent', userSelect: 'none', '&:active': { opacity: 0.85 } }),
      }}
    >
      <Box sx={{ px: 1.5, py: 0.75, display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <span style={{ margin: '-10px -12px', flexShrink: 0, display: 'inline-flex' }}><ThermometerIcon size={42} temperature={weather.temperature} /></span>
        <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1, flexShrink: 0, color: 'text.primary', ml: 1 }}>
          {weather.temperature != null ? `${convertTemp(weather.temperature, temperatureUnit)}${tempUnitLabel(temperatureUnit)}` : ''}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, ml: 'auto', minWidth: 0 }}>
          {weather.shortForecast && (
            <Typography noWrap sx={{ fontSize: '0.7rem', color: 'text.secondary', textAlign: 'right' }}>
              {weather.shortForecast}
            </Typography>
          )}
          {metrics.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
              {metrics}
            </Box>
          )}
        </Box>
      </Box>
    </Card>
  );
});

/** Diagonal line separator for pill weather cards */
const DiagonalDivider: React.FC = () => (
  <svg width="12" height="36" viewBox="0 0 12 36" style={{ flexShrink: 0 }}>
    <line x1="10" y1="0" x2="2" y2="36" stroke="rgba(0,0,0,0.12)" strokeWidth="1" />
  </svg>
);

interface WeatherDetailContentProps {
  weather: MatchWeatherData;
  matchTime?: string | null;
}

/**
 * Light-themed weather detail content for popover (desktop) and drawer (mobile).
 * Shows hourly forecast timeline only — metrics are already on the inline card.
 */
export const WeatherDetailContent: React.FC<WeatherDetailContentProps> = ({ weather, matchTime }) => {
  const temperatureUnit = useSelector((state: RootState) => state.auth.user?.temperatureUnit) || 'FAHRENHEIT';
  const matchHourIso = matchTime ? roundToNearestHour(new Date(matchTime)).toISOString() : null;

  const uvSrc = weather.uvIndex != null
    ? `/weather-icons/uv-index-${Math.min(11, Math.max(1, Math.round(weather.uvIndex)))}.svg?v=${WEATHER_ICON_VERSION}`
    : null;

  const windBeaufort = weather.windSpeed ? getBeaufortNumber(weather.windSpeed) : null;
  const windSrc = windBeaufort != null
    ? `/weather-icons/wind-beaufort-${windBeaufort}.svg?v=${WEATHER_ICON_VERSION}`
    : null;

  const alert = getWeatherAlert(weather);

  return (
    <Box sx={{ px: 2, pt: 1.5, pb: 3, minWidth: 260 }}>
      <Typography sx={{ fontSize: '15px', fontWeight: 700, color: 'text.primary', mb: 0.5, pr: 4 }}>Match Day Weather</Typography>
      {alert && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {alert.headline}{alert.tip ? ` — ${alert.tip}` : ''}
        </Typography>
      )}

      {/* UV + Wind + AQI summary row */}
      {(uvSrc || windSrc || weather.aqi != null) && (
        <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 1.5 }}>
          {uvSrc && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <img src={uvSrc} width={48} height={48} alt="" />
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.2 }}>
                  UV
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {getUvLabel(weather.uvIndex!)}
                </Typography>
              </Box>
            </Box>
          )}
          {weather.aqi != null && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: getAqiColor(weather.aqi), flexShrink: 0 }} />
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.2 }}>
                  AQI {weather.aqi}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {weather.aqiCategory || getAqiLabel(weather.aqi)}
                </Typography>
              </Box>
            </Box>
          )}
          {windSrc && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <img src={windSrc} width={48} height={48} alt="" />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.2 }}>
                  Wind
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {getBeaufortLabel(weather.windSpeed!)}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {weather.hourly && weather.hourly.length > 0 && (() => {
        // Show 5 hours: 2 before match, match hour, 2 after
        const matchIdx = matchHourIso
          ? weather.hourly.findIndex(h => roundToNearestHour(new Date(h.time)).toISOString() === matchHourIso)
          : -1;
        const startIdx = matchIdx >= 0 ? Math.max(0, matchIdx - 2) : 0;
        const visibleHours = weather.hourly.slice(startIdx, startIdx + 5);
        return (
          <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'center' }}>
            {visibleHours.map((h) => {
              const dt = new Date(h.time);
              const isMatchHour = matchHourIso !== null && roundToNearestHour(dt).toISOString() === matchHourIso;
              const hIsDaytime = dt.getHours() >= 6 && dt.getHours() < 18;
              const { src: hSrc } = getWeatherIcon(h.shortForecast, hIsDaytime);
              return (
                <Box key={h.time} sx={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  flex: 1, py: 1, px: 0.5, borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  ...(isMatchHour && { borderColor: 'rgba(173, 216, 255, 0.8)' }),
                }}>
                  <Typography variant="caption" sx={{ fontWeight: isMatchHour ? 700 : 500, color: isMatchHour ? 'text.primary' : 'text.secondary', fontSize: '0.8rem', ...(isMatchHour && { transform: 'scale(1.2)' }) }}>
                    {formatHourLabel(dt)}
                  </Typography>
                  <img src={hSrc} alt="" style={{ width: '100%', maxWidth: 64, aspectRatio: '1', margin: '4px 0' }} />
                  <Typography sx={{ fontWeight: isMatchHour ? 700 : 600, fontSize: '1rem', lineHeight: 1.2, color: isMatchHour ? 'text.primary' : 'text.secondary', ...(isMatchHour && { transform: 'scale(1.1)' }) }}>
                    {convertTemp(h.temperature, temperatureUnit)}{tempUnitLabel(temperatureUnit)}
                  </Typography>
                  {h.precipChance != null && h.precipChance > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mt: 0.5 }}>
                      <span style={{ fontSize: 13 }}>☔</span>
                      <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem', lineHeight: 1, fontWeight: 600 }}>
                        {h.precipChance}%
                      </Typography>
                    </Box>
                  )}
                  {h.humidity != null && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mt: 0.4 }}>
                      <WaterDrop sx={{ fontSize: 13, color: 'text.disabled' }} />
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1 }}>
                        {h.humidity}%
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        );
      })()}
    </Box>
  );
};

function roundToNearestHour(date: Date): Date {
  const d = new Date(date);
  if (d.getMinutes() >= 30) d.setHours(d.getHours() + 1);
  d.setMinutes(0, 0, 0);
  return d;
}

function formatHourLabel(date: Date): string {
  const h = date.getHours();
  if (h === 0) return '12a';
  if (h < 12) return `${h}a`;
  if (h === 12) return '12p';
  return `${h - 12}p`;
}

/** Convert NWS wind speed string to Beaufort scale label */
function getBeaufortLabel(windSpeed: string): string {
  const nums = windSpeed.match(/\d+/g);
  if (!nums) return windSpeed;
  const mph = parseInt(nums[nums.length - 1], 10);
  if (mph < 1) return 'Calm';
  if (mph <= 7) return 'Light';
  if (mph <= 12) return 'Gentle';
  if (mph <= 18) return 'Moderate';
  if (mph <= 24) return 'Fresh';
  if (mph <= 31) return 'Strong';
  if (mph <= 38) return 'Near Gale';
  return 'Gale+';
}

/** Get descriptive label for UV index */
function getUvLabel(uv: number): string {
  const rounded = Math.round(uv);
  if (rounded <= 2) return 'Low';
  if (rounded <= 5) return 'Moderate';
  if (rounded <= 7) return 'High';
  if (rounded <= 10) return 'Very High';
  return 'Extreme';
}

/** Get descriptive label for AQI value */
function getAqiLabel(aqi: number): string {
  if (aqi <= 50) return 'Good';
  if (aqi <= 100) return 'Moderate';
  if (aqi <= 150) return 'Unhealthy for Sensitive';
  if (aqi <= 200) return 'Unhealthy';
  if (aqi <= 300) return 'Very Unhealthy';
  return 'Hazardous';
}

/** Check if NWS shortForecast mentions precipitation */
function hasPrecipForecast(forecast?: string | null): boolean {
  if (!forecast) return false;
  return /rain|shower|storm|drizzle|thunder|precip|sleet|snow|flurr/i.test(forecast);
}

/** Convert NWS wind speed string to Beaufort scale number (0-12) */
function getBeaufortNumber(windSpeed: string): number {
  const nums = windSpeed.match(/\d+/g);
  if (!nums) return 0;
  const mph = parseInt(nums[nums.length - 1], 10);
  if (mph < 1) return 0;
  if (mph <= 3) return 1;
  if (mph <= 7) return 2;
  if (mph <= 12) return 3;
  if (mph <= 18) return 4;
  if (mph <= 24) return 5;
  if (mph <= 31) return 6;
  if (mph <= 38) return 7;
  if (mph <= 46) return 8;
  if (mph <= 54) return 9;
  if (mph <= 63) return 10;
  if (mph <= 72) return 11;
  return 12;
}
